import React, { useState, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import { EditorField, ADVANCED_SECTION_FIELDS } from '@/config/ecoBuilder/types';
import { Megaphone, Timer, Tag, X, ChevronRight, ChevronLeft, Gift, Truck, ShieldCheck, CreditCard } from 'lucide-react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation, EffectFade, Pagination } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import 'swiper/css';
import 'swiper/css/effect-fade';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

// ---------------------------------------------------------------------------
// 1. Interfaces & Types
// ---------------------------------------------------------------------------

export type AppointmentBarMode = 'static' | 'slider' | 'marquee' | 'countdown' | 'coupon';

export interface SmartAnnouncementBarProps {
    mode?: AppointmentBarMode;
    messages?: {
        text: string;
        link?: string;
        icon?: string;
    }[];
    // Countdown Props
    countdownDate?: string; // ISO date or "tomorrow", "end_of_day"
    countdownText?: string;

    // Styling
    backgroundColor?: string;
    textColor?: string;
    height?: 'compact' | 'flow' | 'tall';
    isSticky?: boolean;
    closable?: boolean;

    // Slider Config
    sliderSpeed?: number;
    sliderEffect?: 'slide' | 'fade';

    // Marquee Config
    marqueeSpeed?: number;

    // Common
    className?: string;
    style?: React.CSSProperties;
}

// ---------------------------------------------------------------------------
// 2. Editor Schema
// ---------------------------------------------------------------------------

export const SmartAnnouncementBarSchema: EditorField[] = [
    {
        key: 'mode',
        label: 'نمط العرض',
        type: 'select',
        options: [
            { value: 'slider', label: 'شريط متحرك (Slider)' }, // Default best practice
            { value: 'marquee', label: 'نص زاحف (Marquee)' },
            { value: 'static', label: 'نص ثابت (Static)' },
            { value: 'countdown', label: 'عداد تنازلي (Countdown)' },
            // { value: 'coupon', label: 'كود خصم (Coupon)' }, // Keeping it simple for now as requested by "5 best practices" focus
        ],
        defaultValue: 'slider'
    },
    {
        key: 'messages',
        label: 'الرسائل / الإعلانات',
        type: 'array',
        description: 'أضف رسائل متعددة للتبديل بينها في وضع السلايدر.',
        arrayConfig: {
            itemLabel: 'إعلان',
            schema: [
                { key: 'text', label: 'نص الإعلان', type: 'text', defaultValue: 'خصم 50% لفترة محدودة!' },
                {
                    key: 'icon',
                    label: 'أيقونة',
                    type: 'select',
                    options: [
                        { value: 'none', label: 'بدون أيقونة' },
                        { value: 'sale', label: 'خصم / تاج (Tag)' },
                        { value: 'gift', label: 'هدية (Gift)' },
                        { value: 'delivery', label: 'توصيل (Truck)' },
                        { value: 'shield', label: 'ضمان (Shield)' },
                        { value: 'card', label: 'دفع (Card)' },
                        { value: 'megaphone', label: 'إعلان (Megaphone)' },
                    ],
                    defaultValue: 'sale'
                },
                { key: 'link', label: 'رابط (اختياري)', type: 'text' }
            ]
        },
        defaultValue: [
            { text: '🚚 توصيل مجاني للطلبات فوق 5000 دج', icon: 'delivery' },
            { text: '🎁 اشتري قطعة واحصل على الثانية مجاناً', icon: 'gift' },
            { text: '⚡ تخفيضات الموسم: خصم يصل إلى 40%', icon: 'sale' }
        ]
    },
    {
        key: 'marqueeSpeed',
        label: 'سرعة التحرك (نص زاحف)',
        type: 'number',
        defaultValue: 20
    },
    {
        key: 'countdownDate',
        label: 'تاريخ انتهاء العرض (لوضع العداد)',
        type: 'date',
        description: 'اختر تاريخ انتهاء العرض.',
        defaultValue: new Date().toISOString()
    },
    {
        key: 'countdownText',
        label: 'نص بجانب العداد',
        type: 'text',
        defaultValue: 'ينتهي العرض خلال:'
    },
    // Styles
    {
        key: 'backgroundColor',
        label: 'لون الخلفية',
        type: 'color',
        defaultValue: '#1e293b' // Slate 800 - Professional Dark
    },
    {
        key: 'textColor',
        label: 'لون النص',
        type: 'color',
        defaultValue: '#ffffff'
    },
    {
        key: 'isSticky',
        label: 'تثبيت الشريط في الأعلى (Sticky)',
        type: 'boolean',
        defaultValue: true
    },
    {
        key: 'closable',
        label: 'إظهار زر الإغلاق',
        type: 'boolean',
        defaultValue: false
    },
    ...ADVANCED_SECTION_FIELDS
];

export const SmartAnnouncementBarDefaultProps: Partial<SmartAnnouncementBarProps> = {
    mode: 'slider',
    backgroundColor: '#1e293b',
    textColor: '#ffffff',
    height: 'compact',
    isSticky: true,
    closable: false,
    sliderSpeed: 3000,
    sliderEffect: 'fade', // Fade looks cleaner for text
    marqueeSpeed: 20,
    messages: [
        { text: '🚚 توصيل مجاني 58 ولاية والدفع عند الاستلام', icon: 'delivery' },
        { text: '🔥 العرض ينتهي قريباً - اطلب الآن', icon: 'sale' },
        { text: '🛡️ ضمان استرجاع الأموال لمدة 30 يوم', icon: 'shield' }
    ],
    countdownDate: 'end_of_day',
    countdownText: 'ينتهي العرض بعد:'
};

// ---------------------------------------------------------------------------
// 3. Components
// ---------------------------------------------------------------------------

const IconMap: Record<string, React.FC<any>> = {
    sale: Tag,
    gift: Gift,
    delivery: Truck,
    shield: ShieldCheck,
    card: CreditCard,
    megaphone: Megaphone,
    none: () => null
};

// Helper for Countdown
// Helper for Countdown
const CountdownTimer = ({ targetDate, textColor }: { targetDate: string, textColor: string }) => {
    const [timeLeft, setTimeLeft] = useState<{ d: number, h: number, m: number, s: number }>({ d: 0, h: 0, m: 0, s: 0 });

    useEffect(() => {
        const calculateTimeLeft = () => {
            const now = new Date().getTime();
            let target = 0;

            if (targetDate === 'end_of_day') {
                const end = new Date();
                end.setHours(23, 59, 59, 999);
                target = end.getTime();
            } else {
                const d = new Date(targetDate);
                // Force end of day for the selected date
                d.setHours(23, 59, 59, 999);

                if (!isNaN(d.getTime())) {
                    target = d.getTime();
                } else {
                    // Fallback to 24h from now if invalid
                    target = now + 24 * 60 * 60 * 1000;
                }
            }

            const distance = target - now;

            if (distance < 0) {
                return { d: 0, h: 0, m: 0, s: 0 };
            }

            return {
                d: Math.floor(distance / (1000 * 60 * 60 * 24)),
                h: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
                m: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
                s: Math.floor((distance % (1000 * 60)) / 1000)
            };
        };

        // Initial calculation
        setTimeLeft(calculateTimeLeft());

        const timer = setInterval(() => {
            setTimeLeft(calculateTimeLeft());
        }, 1000);

        return () => clearInterval(timer);
    }, [targetDate]);

    const boxStyle = {
        backgroundColor: 'rgba(255,255,255,0.15)',
        padding: '0.1rem 0.4rem',
        borderRadius: '4px',
        fontWeight: 'bold',
        minWidth: '24px',
        display: 'inline-block',
        textAlign: 'center' as const,
        color: textColor
    };

    return (
        <div className="flex items-center gap-1 mx-2" dir="ltr">
            {timeLeft.d > 0 && (
                <>
                    <span style={boxStyle}>{timeLeft.d}d</span>
                    <span>:</span>
                </>
            )}
            <span style={boxStyle}>{String(timeLeft.h).padStart(2, '0')}</span>
            <span>:</span>
            <span style={boxStyle}>{String(timeLeft.m).padStart(2, '0')}</span>
            <span>:</span>
            <span style={boxStyle}>{String(timeLeft.s).padStart(2, '0')}</span>
        </div>
    );
};

export const SmartAnnouncementBar: React.FC<SmartAnnouncementBarProps> = ({
    mode = 'slider',
    messages = [],
    backgroundColor = '#1e293b',
    textColor = '#ffffff',
    height = 'compact',
    isSticky = false,
    closable = false,
    sliderSpeed = 3000,
    sliderEffect = 'fade',
    marqueeSpeed = 20,
    countdownDate = 'end_of_day',
    countdownText,
    style,
    className
}) => {
    const [isVisible, setIsVisible] = useState(true);
    const swiperRef = useRef<SwiperType | null>(null);

    if (!isVisible) return null;
    if (!messages || messages.length === 0) return null;

    return (
        <div
            className={cn(
                "w-full transition-all duration-300 z-50 relative border-b border-white/5",
                isSticky ? "sticky top-0" : "relative",
                height === 'compact' ? "py-2.5" : height === 'tall' ? "py-4" : "py-3",
                className
            )}
            style={{ backgroundColor, color: textColor, ...style }}
            dir="rtl"
            id="smart-announcement-bar"
        >
            <div className="container mx-auto px-4 flex items-center justify-center relative min-h-[24px]">

                {/* 1. SLIDER MODE */}
                {mode === 'slider' && (
                    <div className="w-full max-w-2xl mx-auto relative group">
                        <Swiper
                            modules={[Autoplay, Pagination, Navigation, EffectFade]}
                            effect={sliderEffect}
                            fadeEffect={{ crossFade: true }}
                            spaceBetween={30}
                            centeredSlides={true}
                            autoplay={{
                                delay: sliderSpeed,
                                disableOnInteraction: false,
                            }}
                            loop={messages.length > 1}
                            onBeforeInit={(swiper) => {
                                swiperRef.current = swiper;
                            }}
                            className="w-full flex justify-center"
                        >
                            {messages.map((msg, i) => {
                                const Icon = IconMap[msg.icon || 'none'] || IconMap.none;
                                return (
                                    <SwiperSlide key={i} className="flex justify-center items-center">
                                        <div className="flex items-center justify-center gap-2 text-sm md:text-base font-medium text-center">
                                            {Icon && <Icon className="w-4 h-4 md:w-5 md:h-5 shrink-0 opacity-90" />}
                                            <span dangerouslySetInnerHTML={{ __html: msg.text }} />
                                            {msg.link && (
                                                <a href={msg.link} className="underline decoration-white/50 underline-offset-4 hover:decoration-white/100 text-xs font-bold mr-1">
                                                    تصفح الآن
                                                </a>
                                            )}
                                        </div>
                                    </SwiperSlide>
                                )
                            })}
                        </Swiper>

                        {/* Custom Nav Arrows with ref control */}
                        {messages.length > 1 && (
                            <>
                                <button
                                    onClick={() => swiperRef.current?.slideNext()}
                                    className="absolute right-[-30px] top-1/2 -translate-y-1/2 cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity hidden md:flex items-center justify-center z-20 w-8 h-8 hover:bg-white/10 rounded-full"
                                    aria-label="Next"
                                >
                                    <ChevronRight className="w-5 h-5" />
                                </button>
                                <button
                                    onClick={() => swiperRef.current?.slidePrev()}
                                    className="absolute left-[-30px] top-1/2 -translate-y-1/2 cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity hidden md:flex items-center justify-center z-20 w-8 h-8 hover:bg-white/10 rounded-full"
                                    aria-label="Previous"
                                >
                                    <ChevronLeft className="w-5 h-5" />
                                </button>
                            </>
                        )}
                    </div>
                )}

                {/* 2. STATIC MODE */}
                {mode === 'static' && (
                    <div className="flex flex-wrap items-center justify-center gap-2 text-sm md:text-base font-medium text-center">
                        {(() => {
                            const msg = messages?.[0];
                            if (!msg) return null;
                            const Icon = IconMap[msg.icon || 'none'] || IconMap.none;
                            return (
                                <>
                                    {Icon && <Icon className="w-4 h-4 md:w-5 md:h-5 shrink-0 opacity-90" />}
                                    <span dangerouslySetInnerHTML={{ __html: msg.text }} />
                                    {msg.link && (
                                        <a href={msg.link} className="underline decoration-white/50 underline-offset-4 hover:decoration-white/100 text-xs font-bold mr-1">
                                            تصفح الآن
                                        </a>
                                    )}
                                </>
                            );
                        })()}
                    </div>
                )}

                {/* 3. COUNTDOWN MODE */}
                {mode === 'countdown' && (
                    <div className="flex flex-wrap items-center justify-center gap-2 text-sm md:text-base font-medium text-center">
                        <Timer className="w-4 h-4 animate-pulse" />
                        <span className="opacity-90" dangerouslySetInnerHTML={{ __html: countdownText || '' }} />
                        <CountdownTimer targetDate={countdownDate || 'end_of_day'} textColor={textColor} />
                        <span className="hidden md:inline mx-2 opacity-50">|</span>
                        <a href={messages?.[0]?.link || '#'} className="bg-white/10 hover:bg-white/20 transition-colors px-3 py-1 rounded text-xs font-bold">
                            {messages?.[0]?.text || 'اطلب الآن'}
                        </a>
                    </div>
                )}

                {/* 4. MARQUEE MODE (Using CSS keyframes) */}
                {mode === 'marquee' && (
                    <div className="w-full overflow-hidden relative flex items-center">
                        <div className="animate-marquee whitespace-nowrap flex gap-16 min-w-full items-center">
                            {/* Duplicate messages to create continuous loop */}
                            {[...messages, ...messages, ...messages].map((msg, i) => {
                                if (!msg) return null;
                                const Icon = IconMap[msg.icon || 'none'] || IconMap.none;
                                return (
                                    <div key={i} className="flex items-center gap-2 text-sm font-medium">
                                        {Icon && <Icon className="w-4 h-4 shrink-0" />}
                                        <span dangerouslySetInnerHTML={{ __html: msg.text || '' }} />
                                    </div>
                                )
                            })}
                        </div>
                        {/* Inline styles for CSS Marquee if NOT in global CSS */}
                        <style dangerouslySetInnerHTML={{
                            __html: `
                            @keyframes marquee {
                                0% { transform: translateX(0); }
                                100% { transform: translateX(-50%); }
                            }
                            .animate-marquee {
                                animation: marquee ${marqueeSpeed}s linear infinite;
                            }
                        `}} />
                    </div>
                )}

                {/* Close Button */}
                {closable && (
                    <button
                        onClick={() => setIsVisible(false)}
                        className="absolute left-0 top-1/2 -translate-y-1/2 p-1 hover:bg-white/10 rounded-full transition-colors opacity-70 hover:opacity-100"
                        aria-label="Close"
                    >
                        <X className="w-4 h-4" />
                    </button>
                )}

            </div>
        </div>
    );
};
